import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { money } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Tag, Users, Activity, Banknote, CheckCircle2, Trash2, Inbox, Upload, Download, Pencil, Archive } from "lucide-react";
import SubmissionsTab from "@/pages/admin/SubmissionsTab";
import EditedTab from "@/pages/admin/EditedTab";
import PayoutsTab from "@/pages/admin/PayoutsTab";
import WithdrawalsTab from "@/pages/admin/WithdrawalsTab";
import DealsTab from "@/pages/admin/DealsTab";
import WalletsTab from "@/pages/admin/WalletsTab";
import LedgerTab from "@/pages/admin/LedgerTab";
import DomainsTab from "@/pages/admin/DomainsTab";
import UserDetailDialog from "@/pages/admin/UserDetailDialog";
import AllDealsTab from "@/pages/admin/AllDealsTab";
import CategoryImagesTab from "@/pages/admin/CategoryImagesTab";
import ContactTab from "@/pages/admin/ContactTab";
import LegalTab from "@/pages/admin/LegalTab";
import DealFormDialog from "@/pages/admin/DealFormDialog";

const StatCard = ({ icon: Icon, label, value, accent, onClick, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className="text-left rounded-xl border border-black/10 bg-white p-5 transition-all duration-200 hover:-translate-y-0.5 hover:border-black/20 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/40"
  >
    <div className={`flex h-9 w-9 items-center justify-center rounded-md ${accent}`}>
      <Icon className="h-4 w-4" />
    </div>
    <p className="mt-3 font-heading text-3xl" style={{ fontWeight: 700 }}>{value}</p>
    <p className="text-sm text-muted-foreground">{label}</p>
  </button>
);

export default function AdminDashboard() {
  const dealDialog = useRef(null);
  const [stats, setStats] = useState(null);
  const [deals, setDeals] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [payoutSettings, setPayoutSettings] = useState(null);
  const [pricingSettings, setPricingSettings] = useState(null);
  const [pricingForm, setPricingForm] = useState({ admin_fee_pct: "", processing_estimate_pct: "", default_province: "ON", multipack_flat_fee: "" });
  const [savingPricing, setSavingPricing] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [withdrawals, setWithdrawals] = useState([]);
  const [wdFilter, setWdFilter] = useState("all");
  const [users, setUsers] = useState([]);
  const [userQuery, setUserQuery] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [cleanupForm, setCleanupForm] = useState({ enabled: true, interval_minutes: "10" });
  const [creditAmount, setCreditAmount] = useState("");
  const [creditNote, setCreditNote] = useState("");
  const [crediting, setCrediting] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + "01";
  const [rangeStart, setRangeStart] = useState(monthStart);
  const [rangeEnd, setRangeEnd] = useState(today);
  const [walletSummary, setWalletSummary] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [revenueMonthly, setRevenueMonthly] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [edited, setEdited] = useState([]);
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState("");
  const [newUser, setNewUser] = useState({ email: "", name: "", password: "", role: "user", send_invite: false });
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [ledger, setLedger] = useState(null);
  const [ledgerProvince, setLedgerProvince] = useState("");
  const [userDetail, setUserDetail] = useState(null);
  const [userDetailOpen, setUserDetailOpen] = useState(false);
  const [rollup, setRollup] = useState("day");
  const [tab, setTab] = useState("submissions");
  const [messages, setMessages] = useState([]);
  const [providerInfo, setProviderInfo] = useState(null);
  const [templates, setTemplates] = useState([]);

  const load = async () => {
    const [s, d, p, sub, dom, ed, ps, wd, prc, msgs, prov, tpls, cln] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/deals"),
      api.get("/admin/payouts"),
      api.get("/admin/submissions"),
      api.get("/admin/domains"),
      api.get("/admin/edited"),
      api.get("/admin/settings/payout"),
      api.get("/admin/withdrawals"),
      api.get("/admin/settings/pricing"),
      api.get("/admin/contact-messages"),
      api.get("/admin/payouts/provider"),
      api.get("/admin/reply-templates"),
      api.get("/admin/settings/cleanup"),
    ]);
    setStats(s.data);
    setDeals(d.data);
    setPayouts(p.data);
    setSubmissions(sub.data);
    setDomains(dom.data);
    setEdited(ed.data);
    setPayoutSettings(ps.data);
    setWithdrawals(wd.data);
    setPricingSettings(prc.data);
    setMessages(msgs.data);
    setProviderInfo(prov.data);
    setTemplates(tpls.data);
    setCleanupForm({ enabled: cln.data.enabled, interval_minutes: String(cln.data.interval_minutes) });
    setPricingForm({
      admin_fee_pct: (prc.data.admin_fee_pct * 100).toString(),
      processing_estimate_pct: (prc.data.processing_estimate_pct * 100).toString(),
      default_province: prc.data.default_province,
      multipack_flat_fee: (prc.data.multipack_flat_fee ?? 2).toString(),
    });
  };

  const markReviewed = async (id) => {
    try {
      await api.post(`/admin/deals/${id}/reviewed`);
      toast.success("Marked as reviewed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadUsers(); loadWalletSummary(); loadLedger(); }, []);

  const loadLedger = async (province = ledgerProvince) => {
    const params = { start: rangeStart, end: rangeEnd };
    if (province) params.province = province;
    const { data } = await api.get("/admin/ledger", { params });
    setLedger(data);
  };

  const createUser = async () => {
    const invite = !!newUser.send_invite;
    if (!newUser.email.trim() || !newUser.name.trim()) {
      return toast.error("Name and a valid email are required.");
    }
    if (!invite && newUser.password.length < 6) {
      return toast.error("Set a 6+ char temporary password, or switch to sending an invite link.");
    }
    setCreatingUser(true);
    try {
      const body = invite
        ? { email: newUser.email, name: newUser.name, role: newUser.role, send_invite: true }
        : { email: newUser.email, name: newUser.name, role: newUser.role, password: newUser.password };
      await api.post("/admin/users", body);
      toast.success(invite ? `Invite link sent to ${newUser.email}` : `${newUser.name} added`);
      setUserDialogOpen(false);
      setNewUser({ email: "", name: "", password: "", role: "user", send_invite: false });
      loadUsers(userQuery);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create user");
    } finally {
      setCreatingUser(false);
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Remove ${u.name || u.email}? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success("User removed");
      loadUsers(userQuery);
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      const blocked = /payments in progress|wallet balance/i.test(detail);
      if (err?.response?.status === 400 && blocked) {
        if (window.confirm(`${detail}\n\nForce delete anyway? This removes ${u.name || u.email} and all of their match records. Use only for test/leftover accounts.`)) {
          try {
            await api.delete(`/admin/users/${u.user_id}?force=true`);
            toast.success("User force-deleted");
            loadUsers(userQuery);
          } catch (e2) {
            toast.error(e2?.response?.data?.detail || "Couldn't force-delete user");
          }
        }
        return;
      }
      toast.error(detail || "Couldn't remove user");
    }
  };

  const openUserDetail = async (u) => {
    setUserDetail(null);
    setUserDetailOpen(true);
    try {
      const { data } = await api.get(`/admin/users/${u.user_id}/detail`);
      setUserDetail(data);
    } catch {
      toast.error("Couldn't load user");
      setUserDetailOpen(false);
    }
  };

  const exportLedger = async () => {
    try {
      const params = { start: rangeStart, end: rangeEnd };
      if (ledgerProvince) params.province = ledgerProvince;
      const res = await api.get("/admin/ledger/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `bogoshare_ledger${ledgerProvince ? "_" + ledgerProvince : ""}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Ledger CSV downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  const openEdit = (d) => dealDialog.current?.open(d);

  const purgeTestDeals = async () => {
    if (!window.confirm("Delete all auto-generated test/demo deals (TEST… titles, 'Test Deal' entries, and fake retailers like TestCo / WalletCo) and their matches? This can't be undone.")) return;
    try {
      const { data } = await api.post("/admin/deals/purge-test");
      toast.success(`Removed ${data.deleted} test deal(s).`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Purge failed");
    }
  };


  const bulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/deals/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.created} deal(s)` + (data.errors.length ? ` · ${data.errors.length} skipped` : ""));
      if (data.errors.length) console.warn("CSV skipped rows:", data.errors);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "CSV import failed");
    }
    e.target.value = "";
  };

  const downloadTemplate = () => {
    const header = "title,retailer,category,deal_type,price_type,original_price,deal_price,image_url,description,terms,location_scope,province,city,address,expires_at,both_present_required,source";
    const sample = "BOGO Coffee,BeanCo,Groceries,Buy One Get One Free,fixed,20,0,,Buy one get one free,In-store only,city,Ontario,Toronto,123 King St,2026-09-01,true,Weekly flyer";
    const blob = new Blob([header + "\n" + sample + "\n"], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "bogoshare_bulk_template.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const closeDeal = async (id) => {
    await api.delete(`/admin/deals/${id}`);
    toast.success("Deal closed");
    load();
  };

  const deleteDeal = async (d) => {
    if (!window.confirm(`Permanently delete "${d.title}"? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/deals/${d.deal_id}?hard=true`);
      toast.success("Deal deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete deal");
    }
  };

  const bulkDeleteDeals = async (ids) => {
    if (!ids.length) return;
    if (!window.confirm(`Permanently delete ${ids.length} deal${ids.length > 1 ? "s" : ""}? This can't be undone.`)) return;
    try {
      const { data } = await api.post("/admin/deals/bulk-delete", { deal_ids: ids });
      if (data.deleted_count) toast.success(`Deleted ${data.deleted_count} deal${data.deleted_count > 1 ? "s" : ""}`);
      if (data.skipped?.length) {
        toast.warning(`${data.skipped.length} skipped — payments in progress`);
      } else if (!data.deleted_count) {
        toast.info("Nothing was deleted");
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete deals");
    }
  };

  const bulkCloseDeals = async (ids) => {
    if (!ids.length) return;
    if (!window.confirm(`Close ${ids.length} deal${ids.length > 1 ? "s" : ""}? They'll stop accepting new partners.`)) return;
    try {
      const { data } = await api.post("/admin/deals/bulk-close", { deal_ids: ids });
      toast.success(data.closed_count ? `Closed ${data.closed_count} deal${data.closed_count > 1 ? "s" : ""}` : "Nothing to close");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't close deals");
    }
  };

  const fetchArchivedDeals = async () => {
    const { data } = await api.get("/admin/deals", { params: { include_archived: true } });
    return data.filter((d) => d.status === "archived");
  };

  const saveCleanupSettings = async () => {
    try {
      const mins = Math.max(1, Math.min(1440, parseInt(cleanupForm.interval_minutes, 10) || 10));
      const { data } = await api.put("/admin/settings/cleanup", { enabled: cleanupForm.enabled, interval_minutes: mins });
      setCleanupForm({ enabled: data.enabled, interval_minutes: String(data.interval_minutes) });
      toast.success("Auto-cleanup settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save settings");
    }
  };

  const payout = async (id) => {
    try {
      await api.post(`/admin/matches/${id}/payout`);
      toast.success("Payout released");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const holdPayout = async (id) => {
    try {
      await api.post(`/admin/matches/${id}/payout/hold`);
      toast.success("Payout held for manual review");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const toggleAutoPayout = async (enabled) => {
    try {
      const { data } = await api.put("/admin/settings/payout", { auto_payout_enabled: enabled });
      setPayoutSettings((prev) => ({ ...prev, ...data }));
      toast.success(`Auto-payout ${enabled ? "enabled" : "disabled"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const setPayoutProvider = async (prov) => {
    try {
      await api.put("/admin/settings/payout", {
        auto_payout_enabled: !!payoutSettings?.auto_payout_enabled,
        payout_provider: prov,
      });
      const { data } = await api.get("/admin/payouts/provider");
      setProviderInfo(data);
      setPayoutSettings((prev) => ({ ...prev, payout_provider: prov }));
      toast.success(prov === "zum" ? "Switched to Zum Rails" : "Switched to manual e-Transfer");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't switch rail");
    }
  };

  const saveTemplate = async (tpl) => {
    try {
      if (tpl.template_id) {
        await api.put(`/admin/reply-templates/${tpl.template_id}`, { title: tpl.title, body: tpl.body });
      } else {
        await api.post("/admin/reply-templates", { title: tpl.title, body: tpl.body });
      }
      const { data } = await api.get("/admin/reply-templates");
      setTemplates(data);
      toast.success("Template saved");
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save template");
      return false;
    }
  };

  const deleteTemplate = async (id) => {
    try {
      await api.delete(`/admin/reply-templates/${id}`);
      setTemplates((prev) => prev.filter((t) => t.template_id !== id));
      toast.success("Template deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete template");
    }
  };

  const saveAutoPayoutMax = async () => {
    const val = parseFloat(payoutSettings?.max_fee);
    if (isNaN(val) || val < 0) return toast.error("Enter a valid amount");
    try {
      const { data } = await api.put("/admin/settings/payout", {
        auto_payout_enabled: payoutSettings.auto_payout_enabled,
        auto_payout_max_fee: val,
      });
      setPayoutSettings((prev) => ({ ...prev, ...data, max_fee: data.auto_payout_max_fee }));
      toast.success(`Auto-payout limit set to $${data.auto_payout_max_fee}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const sendPayoutDigest = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/admin/payouts/digest/send");
      if (data.sent) {
        toast.success(`Digest emailed to ${data.recipient} · ${data.count} transfer(s), ${money(data.total)}`);
      } else if (!data.email_configured) {
        toast.error("Email isn't configured yet. Add your Resend API key to enable the digest.");
      } else {
        toast.error("Couldn't send the digest email. Check the Resend key / sender.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send digest");
    } finally { setSendingDigest(false); }
  };

  const savePricing = async () => {
    setSavingPricing(true);
    try {
      const { data } = await api.put("/admin/settings/pricing", {
        admin_fee_pct: (parseFloat(pricingForm.admin_fee_pct) || 0) / 100,
        processing_estimate_pct: (parseFloat(pricingForm.processing_estimate_pct) || 0) / 100,
        default_province: pricingForm.default_province,
        multipack_flat_fee: parseFloat(pricingForm.multipack_flat_fee) || 0,
      });
      setPricingSettings(data);
      toast.success("Pricing settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally { setSavingPricing(false); }
  };

  const releaseWithdrawal = async (txnId) => {
    try {
      await api.post(`/admin/withdrawals/${txnId}/release`);
      toast.success("Withdrawal released");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const uploadManualStatus = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/payments/manual/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Updated ${data.updated} payment(s)` + (data.errors.length ? ` · ${data.errors.length} skipped` : ""));
      if (data.errors.length) console.warn("Manual payment upload skipped:", data.errors);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
    e.target.value = "";
  };

  const loadUsers = async (q = "") => {
    const { data } = await api.get("/admin/users", { params: q ? { q } : {} });
    setUsers(data);
  };

  const loadWalletSummary = async (s = rangeStart, e = rangeEnd) => {
    try {
      const [ws, rev, mth] = await Promise.all([
        api.get("/admin/wallet/summary", { params: { start: s, end: e } }),
        api.get("/admin/revenue/summary", { params: { start: s, end: e } }),
        api.get("/admin/revenue/monthly", { params: { months: 6 } }),
      ]);
      setWalletSummary(ws.data);
      setRevenue(rev.data);
      setRevenueMonthly(mth.data);
    } catch (err) { console.debug("admin summary load skipped:", err?.message); }
  };

  const toggleUser = (id) =>
    setSelectedUsers((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const toggleAllUsers = () =>
    setSelectedUsers((prev) => (prev.length === users.length ? [] : users.map((u) => u.user_id)));

  const bulkDeleteUsers = async () => {
    if (!selectedUsers.length) return;
    if (!window.confirm(`Permanently delete ${selectedUsers.length} user${selectedUsers.length > 1 ? "s" : ""}? This can't be undone.`)) return;
    try {
      const { data } = await api.post("/admin/users/bulk-delete", { user_ids: selectedUsers });
      if (data.deleted_count) toast.success(`Deleted ${data.deleted_count} user${data.deleted_count > 1 ? "s" : ""}`);
      if (data.skipped?.length) toast.warning(`${data.skipped.length} skipped (protected or has balance/active payments)`);
      else if (!data.deleted_count) toast.info("Nothing was deleted");
      setSelectedUsers([]);
      loadUsers(userQuery);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete users");
    }
  };

  const bulkCredit = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/wallet/credit/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Credited ${data.credited} wallet(s) · ${money(data.total)} total` + (data.errors.length ? ` · ${data.errors.length} skipped` : ""));
      if (data.errors.length) console.warn("Bulk credit skipped rows:", data.errors);
      loadUsers(userQuery);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk credit failed");
    }
    e.target.value = "";
  };

  const downloadCreditTemplate = () => {
    const csv = "email,amount,note\nuser@example.com,25,Welcome bonus\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "bogoshare_wallet_credit_template.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const exportWalletActivity = async () => {
    try {
      const { data } = await api.get("/admin/wallet/transactions/export", {
        params: { start: rangeStart, end: rangeEnd }, responseType: "blob",
      });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(data);
      a.download = `bogoshare_wallet_activity_${rangeStart}_${rangeEnd}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Wallet activity exported");
    } catch {
      toast.error("Export failed");
    }
  };

  const exportCsv = async (path, filename) => {
    try {
      const { data } = await api.get(path, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(data);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Exported");
    } catch {
      toast.error("Export failed");
    }
  };

  const creditWallets = async () => {
    const amt = parseFloat(creditAmount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount.");
    if (selectedUsers.length === 0) return toast.error("Select at least one user.");
    setCrediting(true);
    try {
      const { data } = await api.post("/admin/wallet/credit", {
        user_ids: selectedUsers, amount: amt, note: creditNote || undefined,
      });
      toast.success(`Added ${money(amt)} to ${data.credited} wallet${data.credited === 1 ? "" : "s"}.`);
      setSelectedUsers([]); setCreditAmount(""); setCreditNote("");
      loadUsers(userQuery);
      loadWalletSummary();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Transfer failed");
    } finally {
      setCrediting(false);
    }
  };

  const approveSub = async (id) => {
    try {
      await api.post(`/admin/submissions/${id}/approve`);
      toast.success("Deal approved & published");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const rejectSub = async (id) => {
    const reason = window.prompt("Reason for rejecting (optional):") || "";
    try {
      await api.post(`/admin/submissions/${id}/reject`, { reason });
      toast.success("Submission rejected");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const bulkApproveDeals = async (ids) => {
    if (!ids.length) return;
    try {
      const { data } = await api.post("/admin/deals/bulk-approve", { deal_ids: ids });
      toast.success(data.approved_count ? `Approved ${data.approved_count} submission${data.approved_count > 1 ? "s" : ""}` : "No pending submissions in selection");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't approve");
    }
  };

  const bulkRejectDeals = async (ids) => {
    if (!ids.length) return;
    const reason = window.prompt("Reason for rejecting the selected submissions (optional):") || "";
    try {
      const { data } = await api.post("/admin/deals/bulk-reject", { deal_ids: ids, reason: reason || "Rejected by admin" });
      toast.success(data.rejected_count ? `Rejected ${data.rejected_count} submission${data.rejected_count > 1 ? "s" : ""}` : "No pending submissions in selection");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't reject");
    }
  };

  const unarchiveDeal = async (d) => {
    try {
      await api.post(`/admin/deals/${d.deal_id}/unarchive`);
      toast.success(`"${d.title}" restored to active`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't restore deal");
    }
  };

  const addDomain = async () => {
    if (!newDomain.trim()) return;
    try {
      await api.post("/admin/domains", { domain: newDomain.trim() });
      toast.success("Trusted domain added");
      setNewDomain("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const removeDomain = async (d) => {
    await api.delete(`/admin/domains/${encodeURIComponent(d)}`);
    toast.success("Domain removed");
    load();
  };

  return (
    <div className="min-h-screen bg-background grain-bg">
      <Navbar />
      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-4xl tracking-tight" style={{ fontWeight: 900 }}>Admin</h1>
            <p className="mt-2 text-muted-foreground">Curate deals and manage e-transfer payouts.</p>
          </div>
          <div className="flex items-center gap-2">
            <input type="file" accept=".csv" id="bulk-csv-input" data-testid="bulk-csv-input" onChange={bulkUpload} className="hidden" />
            <Button variant="outline" data-testid="bulk-csv-btn" onClick={() => document.getElementById("bulk-csv-input").click()}
              className="rounded-full border-black/20">
              <Upload className="mr-1 h-4 w-4" /> Bulk CSV
            </Button>
            <Button variant="ghost" size="sm" data-testid="csv-template-btn" onClick={downloadTemplate} className="text-muted-foreground">
              <Download className="mr-1 h-4 w-4" /> Template
            </Button>
            <Button variant="ghost" size="sm" data-testid="purge-test-btn" onClick={purgeTestDeals} className="text-muted-foreground hover:text-red-600">
              <Trash2 className="mr-1 h-4 w-4" /> Purge test deals
            </Button>
            <DealFormDialog ref={dealDialog} onSaved={load} />
          </div>
        </div>

        {stats && (
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-8 gap-4">
            <StatCard icon={Tag} label="Active deals" value={stats.total_deals} accent="bg-primary/10 text-primary" testid="stat-active-deals" onClick={() => setTab("deals")} />
            <StatCard icon={Users} label="Users" value={stats.total_users} accent="bg-secondary text-foreground" testid="stat-users" onClick={() => setTab("wallets")} />
            <StatCard icon={Activity} label="Active splits" value={stats.active_matches} accent="bg-blue-100 text-blue-700" testid="stat-active-splits" onClick={() => setTab("payouts")} />
            <StatCard icon={Inbox} label="Submissions" value={stats.pending_submissions} accent="bg-purple-100 text-purple-700" testid="stat-submissions" onClick={() => setTab("submissions")} />
            <StatCard icon={Pencil} label="Community edits" value={stats.community_edits || 0} accent="bg-orange-100 text-orange-700" testid="stat-community-edits" onClick={() => setTab("edited")} />
            <StatCard icon={Banknote} label="Pending payouts" value={stats.pending_payouts} accent="bg-amber-100 text-amber-700" testid="stat-pending-payouts" onClick={() => setTab("payouts")} />
            <StatCard icon={CheckCircle2} label="Completed" value={stats.completed_deals} accent="bg-accent/10 text-accent" testid="stat-completed" onClick={() => setTab("all-deals")} />
            <StatCard icon={Archive} label="Archived" value={stats.archived_deals || 0} accent="bg-slate-100 text-slate-700" testid="stat-archived" onClick={() => setTab("all-deals")} />
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab} className="mt-8">
          <TabsList>
            <TabsTrigger value="submissions" data-testid="tab-submissions">
              Submissions{submissions.length ? ` (${submissions.length})` : ""}
            </TabsTrigger>
            <TabsTrigger value="edited" data-testid="tab-edited">
              Community edits{edited.length ? ` (${edited.length})` : ""}
            </TabsTrigger>
            <TabsTrigger value="payouts" data-testid="tab-payouts">Payouts</TabsTrigger>
            <TabsTrigger value="withdrawals" data-testid="tab-withdrawals">
              Withdrawals{withdrawals.filter((w) => w.status === "pending").length ? ` (${withdrawals.filter((w) => w.status === "pending").length})` : ""}
            </TabsTrigger>
            <TabsTrigger value="deals" data-testid="tab-deals">Deals</TabsTrigger>
            <TabsTrigger value="all-deals" data-testid="tab-all-deals">All deals</TabsTrigger>
            <TabsTrigger value="images" data-testid="tab-images">Default images</TabsTrigger>
            <TabsTrigger value="wallets" data-testid="tab-wallets">Wallets</TabsTrigger>
            <TabsTrigger value="ledger" data-testid="tab-ledger">Ledger</TabsTrigger>
            <TabsTrigger value="domains" data-testid="tab-domains">Trusted domains</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages">
              Messages{messages.filter((m) => !m.read).length ? ` (${messages.filter((m) => !m.read).length})` : ""}
            </TabsTrigger>
            <TabsTrigger value="legal" data-testid="tab-legal">Legal</TabsTrigger>
          </TabsList>

          <TabsContent value="submissions" className="mt-6">
            <SubmissionsTab submissions={submissions} approveSub={approveSub} rejectSub={rejectSub} />
          </TabsContent>

          <TabsContent value="edited" className="mt-6">
            <EditedTab edited={edited} openEdit={openEdit} markReviewed={markReviewed} />
          </TabsContent>


          <TabsContent value="payouts" className="mt-6">
            <PayoutsTab
              payouts={payouts} payoutSettings={payoutSettings} setPayoutSettings={setPayoutSettings}
              pricingSettings={pricingSettings} pricingForm={pricingForm} setPricingForm={setPricingForm}
              savingPricing={savingPricing} exportCsv={exportCsv} toggleAutoPayout={toggleAutoPayout}
              saveAutoPayoutMax={saveAutoPayoutMax} savePricing={savePricing} payout={payout} holdPayout={holdPayout}
              providerInfo={providerInfo} setPayoutProvider={setPayoutProvider}
            />
          </TabsContent>

          <TabsContent value="withdrawals" className="mt-6">
            <WithdrawalsTab
              withdrawals={withdrawals} wdFilter={wdFilter} setWdFilter={setWdFilter} exportCsv={exportCsv}
              uploadManualStatus={uploadManualStatus} sendPayoutDigest={sendPayoutDigest}
              sendingDigest={sendingDigest} releaseWithdrawal={releaseWithdrawal}
            />
          </TabsContent>


          <TabsContent value="deals" className="mt-6">
            <DealsTab deals={deals} openEdit={openEdit} closeDeal={closeDeal} deleteDeal={deleteDeal} />
          </TabsContent>

          <TabsContent value="all-deals" className="mt-6">
            <AllDealsTab deals={deals} approveSub={approveSub} rejectSub={rejectSub} openEdit={openEdit} deleteDeal={deleteDeal} bulkDeleteDeals={bulkDeleteDeals} bulkCloseDeals={bulkCloseDeals} bulkApproveDeals={bulkApproveDeals} bulkRejectDeals={bulkRejectDeals} unarchiveDeal={unarchiveDeal} fetchArchivedDeals={fetchArchivedDeals} cleanupForm={cleanupForm} setCleanupForm={setCleanupForm} saveCleanupSettings={saveCleanupSettings} cleanupStats={{ archived: stats?.archived_deals || 0, autoCleaned: stats?.auto_cleaned_total || 0 }} />
          </TabsContent>

          <TabsContent value="images" className="mt-6">
            <CategoryImagesTab />
          </TabsContent>

          <TabsContent value="wallets" className="mt-6">
            <WalletsTab
              walletSummary={walletSummary} revenue={revenue} revenueMonthly={revenueMonthly}
              rangeStart={rangeStart} rangeEnd={rangeEnd} setRangeStart={setRangeStart} setRangeEnd={setRangeEnd}
              loadWalletSummary={loadWalletSummary} exportCsv={exportCsv} bulkCredit={bulkCredit}
              downloadCreditTemplate={downloadCreditTemplate} exportWalletActivity={exportWalletActivity}
              creditAmount={creditAmount} setCreditAmount={setCreditAmount} creditNote={creditNote} setCreditNote={setCreditNote}
              crediting={crediting} creditWallets={creditWallets} userQuery={userQuery} setUserQuery={setUserQuery}
              loadUsers={loadUsers} users={users} selectedUsers={selectedUsers} toggleUser={toggleUser}
              toggleAllUsers={toggleAllUsers} bulkDeleteUsers={bulkDeleteUsers}
              userDialogOpen={userDialogOpen} setUserDialogOpen={setUserDialogOpen} newUser={newUser} setNewUser={setNewUser}
              createUser={createUser} creatingUser={creatingUser} openUserDetail={openUserDetail} removeUser={removeUser}
            />
          </TabsContent>


          <TabsContent value="ledger" className="mt-6">
            <LedgerTab
              ledger={ledger} ledgerProvince={ledgerProvince} setLedgerProvince={setLedgerProvince}
              rangeStart={rangeStart} rangeEnd={rangeEnd} setRangeStart={setRangeStart} setRangeEnd={setRangeEnd}
              loadLedger={loadLedger} exportLedger={exportLedger} rollup={rollup} setRollup={setRollup}
            />
          </TabsContent>


          <TabsContent value="domains" className="mt-6">
            <DomainsTab
              domains={domains} newDomain={newDomain} setNewDomain={setNewDomain}
              addDomain={addDomain} removeDomain={removeDomain}
            />
          </TabsContent>

          <TabsContent value="messages" className="mt-6">
            <ContactTab messages={messages} reload={load} templates={templates}
              saveTemplate={saveTemplate} deleteTemplate={deleteTemplate} />
          </TabsContent>

          <TabsContent value="legal" className="mt-6">
            <LegalTab />
          </TabsContent>
        </Tabs>

        <UserDetailDialog userDetail={userDetail} userDetailOpen={userDetailOpen} setUserDetailOpen={setUserDetailOpen} />
      </main>
    </div>
  );
}
